import './App.css';
import { Fragment, useEffect, useState, useContext } from 'react';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Header from './components/Layout/Header';
import Help from './components/Board/Help';
import Board from './components/Board/Board';
import StateContext from './store/state-context';
import { getGameTypes } from './lib/api';
import { getThemeForSlug, getColourBlindTheme } from './theme/themeRegistry';

const MAX_GUESSES = 8;
const STATUS_IN_PROGRESS = 'IN_PROGRESS';
const STATUS_WON = 'COMPLETED_WON';
const STATUS_LOST = 'COMPLETED_LOST';

const PREFERRED_GAME_TYPE_KEY = 'guessaulenko-preferred-game-type';
const COLOUR_BLIND_MODE_KEY = 'guessaulenko-colour-blind-mode';

const flushState = (state) => {
  return state && !state.game_id;
}

const App = (props) => {
  const [ showHelp, setShowHelp ] = useState(false);
  const [ showResults, setShowResults ] = useState(false);
  const [ availableGameTypes, setAvailableGameTypes ] = useState([]);
  const [ activeGameTypeSlug, setActiveGameTypeSlug ] = useState(
    () => localStorage.getItem(PREFERRED_GAME_TYPE_KEY) || 'classic'
  );
  const [ colourBlindMode, setColourBlindMode ] = useState(
    () => localStorage.getItem(COLOUR_BLIND_MODE_KEY) === 'true'
  );
  const [ gameMaxGuesses, setGameMaxGuesses ] = useState(null);
  const [ gameColumnConfig, setGameColumnConfig ] = useState(null);
  const [ gameIsLoading, setGameIsLoading ] = useState(true);

	const stateCtx = useContext(StateContext);

  useEffect(() => {
    const state = stateCtx.retrieveState(activeGameTypeSlug);

    if (flushState(state)) {
      stateCtx.deleteState(activeGameTypeSlug);
      window.location.reload();
    }

    const stats = stateCtx.retrieveStats(activeGameTypeSlug);
    const hasNeverPlayed = !stats || stats.gamesPlayed === 0;
    const isGameExpired = !state || (state.expiryTs ? state.expiryTs < +new Date() / 1000 : true);

    let timerId;
    if (!isGameExpired) {
      const isGameCompleted = state.status === STATUS_WON || state.status === STATUS_LOST;
      if (isGameCompleted) {
        timerId = setTimeout(() => setShowResults(true), 0);
      }
    } else if (hasNeverPlayed) {
      timerId = setTimeout(() => setShowHelp(true), 0);
    }
    return () => clearTimeout(timerId);
  }, [ stateCtx, activeGameTypeSlug ]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    getGameTypes(controller.signal).then((gameTypes) => {
      if (cancelled) return;
      if (gameTypes.length > 0) {
        setAvailableGameTypes(gameTypes);
        setActiveGameTypeSlug((current) => {
          if (gameTypes.find((gt) => gt.slug === current)) return current;
          const fallback = gameTypes.find((gt) => gt.slug === 'classic')
            ? 'classic'
            : (gameTypes[0]?.slug || 'classic');
          return fallback;
        });
      }
    }).catch((err) => {
      if (err.name === 'AbortError') return;
      console.error('Failed to load game types:', err);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => { setGameMaxGuesses(null); setGameColumnConfig(null); }, [activeGameTypeSlug]);

  const activeGameType = availableGameTypes.find((gt) => gt.slug === activeGameTypeSlug) || null;
  const effectiveMaxGuesses = activeGameType?.max_guesses || gameMaxGuesses || MAX_GUESSES;
  const gameTypeName = activeGameType?.name || (activeGameTypeSlug.charAt(0).toUpperCase() + activeGameTypeSlug.slice(1));

  const themeConfig = colourBlindMode ? getColourBlindTheme() : getThemeForSlug(activeGameTypeSlug);

  useEffect(() => {
    Object.entries(themeConfig.cssVars).forEach(([prop, value]) => {
      document.documentElement.style.setProperty(prop, value);
    });
  }, [activeGameTypeSlug, colourBlindMode, themeConfig]);

  const handleColourBlindToggle = () => {
    setColourBlindMode((prev) => {
      const next = !prev;
      localStorage.setItem(COLOUR_BLIND_MODE_KEY, String(next));
      return next;
    });
  };

  const handleGameTypeChange = (event, newSlug) => {
    const state = stateCtx.retrieveState(newSlug);
    const stats = stateCtx.retrieveStats(newSlug);
    const isGameExpired = !state || (state.expiryTs ? state.expiryTs < +new Date() / 1000 : true);
    const hasNeverPlayed = !stats || stats.gamesPlayed === 0;

    setActiveGameTypeSlug(newSlug);
    setShowHelp(false);
    setShowResults(false);
    setGameMaxGuesses(null);
    setGameColumnConfig(null);
    setGameIsLoading(true);
    localStorage.setItem(PREFERRED_GAME_TYPE_KEY, newSlug);

    if (!isGameExpired) {
      const isGameCompleted = state.status === STATUS_WON || state.status === STATUS_LOST;
      if (isGameCompleted) setShowResults(true);
    } else if (hasNeverPlayed) {
      setShowHelp(true);
    }
  };

  return (
    <ThemeProvider theme={themeConfig.muiTheme}>
      <CssBaseline />
      {themeConfig.globalStyles}
      <Fragment>
			<Header
        showHelp={showHelp} setShowHelp={setShowHelp}
        showResults={showResults} setShowResults={setShowResults}
        availableGameTypes={availableGameTypes}
        activeGameTypeSlug={activeGameTypeSlug}
        onGameTypeChange={handleGameTypeChange}
        colourBlindMode={colourBlindMode}
        onColourBlindToggle={handleColourBlindToggle}
      />
      <Help showHelp={showHelp} setShowHelp={setShowHelp} MAX_GUESSES={effectiveMaxGuesses} gameType={activeGameType} columnConfig={gameColumnConfig} colorLabels={themeConfig.colorLabels} isLoading={gameIsLoading} />
      <Board gameTypeSlug={activeGameTypeSlug} gameTypeName={gameTypeName} showResults={showResults} setShowResults={setShowResults} setShowHelp={setShowHelp} MAX_GUESSES={effectiveMaxGuesses} STATUS_IN_PROGRESS={STATUS_IN_PROGRESS} STATUS_WON={STATUS_WON} STATUS_LOST={STATUS_LOST} onMaxGuessesChange={setGameMaxGuesses} onColumnConfigChange={setGameColumnConfig} onLoadingChange={setGameIsLoading} />
    </Fragment>
    </ThemeProvider>
  );
}

export default App;
